// Tutorials List Page - Admin view with tree and grid layout
// Entry point from admin dashboard "Tutoriels" tab
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Search, FileText, FolderOpen, BookOpen, Eye, EyeOff,
  MoreHorizontal, Copy, Trash2, Clock, User, Loader2,
  LayoutGrid, LayoutList, ChevronRight, Download, FileDown,
  Filter, Tag, ImagePlus, Link as LinkIcon, X, Check,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { tutorialAPI } from '@/lib/api';
import useTutorialStore from '@/stores/tutorialStore';
import { formatRelativeTime } from '@/lib/utils';
import './tutorials.css';

export const TUTORIAL_CATEGORIES = [
  { id: 'all', label: 'Tous', icon: '📚' },
  { id: 'Docker', label: 'Docker', icon: '🐳' },
  { id: 'Kubernetes', label: 'Kubernetes', icon: '☸️' },
  { id: 'AWS', label: 'AWS', icon: '☁️' },
  { id: 'DevOps', label: 'DevOps', icon: '♾️' },
  { id: 'CI/CD', label: 'CI/CD', icon: '🚀' },
  { id: 'Linux', label: 'Linux / Shell', icon: '🐧' },
  { id: 'Général', label: 'Général', icon: '📘' },
];

export function getCategoryTheme(category) {
  const cat = (category || '').toLowerCase();
  if (cat.includes('docker')) {
    return {
      bgClass: 'tut-banner-docker',
      badgeClass: 'bg-sky-500/20 text-sky-200 border-sky-400/30',
      pillClass: 'bg-sky-500/10 text-sky-600 border-sky-500/20 dark:text-sky-400',
      icon: '🐳',
      watermarkText: 'DOCKER',
      label: 'Docker',
    };
  }
  if (cat.includes('kubernetes') || cat.includes('k8s')) {
    return {
      bgClass: 'tut-banner-kubernetes',
      badgeClass: 'bg-blue-500/20 text-blue-200 border-blue-400/30',
      pillClass: 'bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400',
      icon: '☸️',
      watermarkText: 'K8S',
      label: 'Kubernetes',
    };
  }
  if (cat.includes('aws')) {
    return {
      bgClass: 'tut-banner-aws',
      badgeClass: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
      pillClass: 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400',
      icon: '☁️',
      watermarkText: 'AWS',
      label: 'AWS',
    };
  }
  if (cat.includes('devops')) {
    return {
      bgClass: 'tut-banner-devops',
      badgeClass: 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30',
      pillClass: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20 dark:text-indigo-400',
      icon: '♾️',
      watermarkText: 'DEVOPS',
      label: 'DevOps',
    };
  }
  if (cat.includes('ci/cd') || cat.includes('cicd')) {
    return {
      bgClass: 'tut-banner-cicd',
      badgeClass: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
      pillClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400',
      icon: '🚀',
      watermarkText: 'CI/CD',
      label: 'CI/CD',
    };
  }
  if (cat.includes('linux') || cat.includes('shell')) {
    return {
      bgClass: 'tut-banner-linux',
      badgeClass: 'bg-slate-500/20 text-slate-200 border-slate-400/30',
      pillClass: 'bg-slate-500/10 text-slate-600 border-slate-500/20 dark:text-slate-400',
      icon: '🐧',
      watermarkText: 'LINUX',
      label: 'Linux / Shell',
    };
  }
  if (cat.includes('jenkins')) {
    return {
      bgClass: 'tut-banner-jenkins',
      badgeClass: 'bg-red-500/20 text-red-200 border-red-400/30',
      pillClass: 'bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400',
      icon: '⚙️',
      watermarkText: 'JENKINS',
      label: 'Jenkins',
    };
  }
  return {
    bgClass: 'tut-banner-general',
    badgeClass: 'bg-purple-500/20 text-purple-200 border-purple-400/30',
    pillClass: 'bg-purple-500/10 text-purple-600 border-purple-500/20 dark:text-purple-400',
    icon: '🏷️',
    watermarkText: (category || 'TUTORIEL').toUpperCase().slice(0, 10),
    label: category || 'Général',
  };
}

export default function TutorialsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    pages, loading, fetchPages, createPage, updatePage, deletePage, duplicatePage,
    toggleStatus, toggleVisibility, searchQuery, setSearchQuery,
    filterStatus, setFilterStatus, expandedNodes, toggleNode,
    exportHTML, exportPDF,
  } = useTutorialStore();

  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'tree'
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Modals for cover image and custom category
  const [coverModalOpen, setCoverModalOpen] = useState(false);
  const [targetPageForCover, setTargetPageForCover] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);

  const [customCatModalOpen, setCustomCatModalOpen] = useState(false);
  const [targetPageForCat, setTargetPageForCat] = useState(null);
  const [newCatName, setNewCatName] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      fetchPages(user.id);
    }
  }, [user?.id, fetchPages]);

  // Compute combined list of preset categories + dynamic custom categories
  const dynamicCategoriesList = useMemo(() => {
    const presetNames = new Set(TUTORIAL_CATEGORIES.map(c => c.id.toLowerCase()));
    const customList = [];
    pages.forEach(p => {
      if (p.category && !presetNames.has(p.category.toLowerCase())) {
        if (!customList.some(c => c.id.toLowerCase() === p.category.toLowerCase())) {
          customList.push({
            id: p.category,
            label: p.category,
            icon: '🏷️',
          });
        }
      }
    });
    return [...TUTORIAL_CATEGORIES, ...customList];
  }, [pages]);

  const handleCreatePage = async () => {
    const page = await createPage({
      title: 'Nouveau tutoriel',
      icon: '📘',
      category: selectedCategory !== 'all' ? selectedCategory : 'Général',
      order: pages.length,
    }, user?.id);
    if (page) {
      navigate(`/tutorials/${page.id}`);
    }
  };

  const handleOpenPage = (pageId) => {
    navigate(`/tutorials/${pageId}`);
  };

  const handleUpdateCategory = async (pageId, category) => {
    if (user?.id) {
      await updatePage(pageId, { category }, user.id);
      toast.success(`Thème mis à jour : ${category}`);
    }
  };

  // Open cover modal
  const handleOpenCoverModal = (page) => {
    setTargetPageForCover(page);
    setCoverImageUrl(page.cover_image || '');
    setCoverModalOpen(true);
  };

  // Save cover image
  const handleSaveCover = async () => {
    if (targetPageForCover && user?.id) {
      await updatePage(targetPageForCover.id, { cover_image: coverImageUrl }, user.id);
      setCoverModalOpen(false);
      toast.success('Image de couverture mise à jour');
    }
  };

  // File upload for cover in modal
  const handleCoverFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image valide');
      return;
    }
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await tutorialAPI.uploadImage(formData, user?.id);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backendUrl}${res.data.url}`;
      setCoverImageUrl(url);
      toast.success('Image téléversée avec succès');
    } catch {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingCover(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Open custom category modal
  const handleOpenCustomCategoryModal = (page = null) => {
    setTargetPageForCat(page);
    setNewCatName('');
    setCustomCatModalOpen(true);
  };

  // Submit custom category
  const handleCreateCustomCategory = async () => {
    const trimmed = newCatName.trim();
    if (!trimmed) {
      toast.error('Veuillez saisir un nom de catégorie');
      return;
    }
    if (targetPageForCat && user?.id) {
      await updatePage(targetPageForCat.id, { category: trimmed }, user.id);
      toast.success(`Catégorie "${trimmed}" appliquée !`);
    } else {
      setSelectedCategory(trimmed);
      toast.success(`Catégorie "${trimmed}" sélectionnée`);
    }
    setCustomCatModalOpen(false);
  };

  // Filter & sort — only show root-level pages (not sub-pages)
  const rootPagesList = pages.filter(p => !p.parent_id);

  const filteredPages = rootPagesList
    .filter(p => {
      if (filterStatus && p.status !== filterStatus) return false;
      if (selectedCategory !== 'all') {
        const pCat = (p.category || 'Général').toLowerCase();
        const selCat = selectedCategory.toLowerCase();
        if (selCat === 'linux' && !pCat.includes('linux') && !pCat.includes('shell')) return false;
        if (selCat === 'ci/cd' && !pCat.includes('ci/cd') && !pCat.includes('cicd')) return false;
        if (selCat !== 'linux' && selCat !== 'ci/cd' && pCat !== selCat && !pCat.includes(selCat)) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          p.title.toLowerCase().includes(q) ||
          (p.preview || '').toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  // Category counts
  const categoryCounts = dynamicCategoriesList.reduce((acc, cat) => {
    if (cat.id === 'all') {
      acc[cat.id] = rootPagesList.length;
    } else {
      const selCat = cat.id.toLowerCase();
      acc[cat.id] = rootPagesList.filter(p => {
        const pCat = (p.category || 'Général').toLowerCase();
        if (selCat === 'linux') return pCat.includes('linux') || pCat.includes('shell');
        if (selCat === 'ci/cd') return pCat.includes('ci/cd') || pCat.includes('cicd');
        return pCat === selCat || pCat.includes(selCat);
      }).length;
    }
    return acc;
  }, {});

  // Build tree for tree view
  const buildTree = (items, parentId = null) => {
    return items
      .filter(p => p.parent_id === parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(page => ({ ...page, children: buildTree(items, page.id) }));
  };

  const rootPages = filterStatus || searchQuery || selectedCategory !== 'all'
    ? filteredPages.map(p => ({ ...p, children: [] }))
    : buildTree(filteredPages);

  // Stats (root pages only)
  const totalPages = rootPagesList.length;
  const publishedCount = rootPagesList.filter(p => p.status === 'published').length;
  const draftCount = rootPagesList.filter(p => p.status === 'draft').length;
  const visibleCount = rootPagesList.filter(p => p.is_visible).length;

  if (loading && pages.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="tut-list-page" data-testid="tutorials-list-page">
      {/* Header */}
      <div className="tut-list-header">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BookOpen size={22} className="text-indigo-500" />
            Tutoriels
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Créez et gérez des tutoriels pédagogiques par thèmes personnalisables (Docker, Kubernetes, AWS, DevOps, etc.)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenCustomCategoryModal()}
            className="gap-1.5 text-xs"
          >
            <Plus size={14} /> Nouvelle catégorie
          </Button>
          <Button onClick={handleCreatePage} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus size={16} /> Nouveau tutoriel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="tut-list-stats">
        <div className="tut-stat-card">
          <div className="tut-stat-value">{totalPages}</div>
          <div className="tut-stat-label">Total</div>
        </div>
        <div className="tut-stat-card">
          <div className="tut-stat-value text-emerald-500">{publishedCount}</div>
          <div className="tut-stat-label">Publiés</div>
        </div>
        <div className="tut-stat-card">
          <div className="tut-stat-value text-amber-500">{draftCount}</div>
          <div className="tut-stat-label">Brouillons</div>
        </div>
        <div className="tut-stat-card">
          <div className="tut-stat-value text-blue-500">{visibleCount}</div>
          <div className="tut-stat-label">Visibles</div>
        </div>
      </div>

      {/* Theme Category Filter Bar */}
      <div className="tut-category-bar mb-4">
        <div className="tut-category-scroll flex items-center gap-2 overflow-x-auto pb-1">
          {dynamicCategoriesList.map(cat => {
            const count = categoryCounts[cat.id] || 0;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`tut-category-pill ${isActive ? 'active' : ''}`}
              >
                <span className="text-base">{cat.icon}</span>
                <span className="font-medium text-xs whitespace-nowrap">{cat.label}</span>
                <span className={`tut-category-count ${isActive ? 'active-count' : ''}`}>
                  {count}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => handleOpenCustomCategoryModal()}
            className="tut-category-pill border-dashed hover:border-solid hover:border-indigo-500 text-indigo-500"
            title="Ajouter une nouvelle catégorie"
          >
            <Plus size={14} />
            <span className="font-medium text-xs whitespace-nowrap">Créer une catégorie</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="tut-list-toolbar">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un tutoriel..."
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? null : v)}>
            <SelectTrigger className="w-36 h-9">
              <Filter size={14} className="mr-1.5" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="published">Publiés</SelectItem>
              <SelectItem value="draft">Brouillons</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex border rounded-md bg-card">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon" className="h-9 w-9 rounded-r-none"
              onClick={() => setViewMode('grid')}
              title="Vue cartes avec couverture"
            >
              <LayoutGrid size={14} />
            </Button>
            <Button
              variant={viewMode === 'tree' ? 'secondary' : 'ghost'}
              size="icon" className="h-9 w-9 rounded-l-none"
              onClick={() => setViewMode('tree')}
              title="Vue arborescente"
            >
              <LayoutList size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {filteredPages.length === 0 ? (
        <div className="tut-list-empty">
          <BookOpen size={48} className="text-muted-foreground/30 mb-3" />
          <h3 className="text-lg font-medium">
            {searchQuery || selectedCategory !== 'all' ? 'Aucun tutoriel trouvé' : 'Aucun tutoriel'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {searchQuery || selectedCategory !== 'all'
              ? 'Essayez avec d\'autres critères de filtre ou de recherche'
              : 'Créez votre premier tutoriel pour commencer'}
          </p>
          <Button onClick={handleCreatePage} className="mt-4 gap-1.5">
            <Plus size={16} /> Créer un tutoriel
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="tut-list-grid">
          <AnimatePresence>
            {filteredPages.map((page, idx) => (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: idx * 0.03 }}
              >
                <PageCard
                  page={page}
                  categoriesList={dynamicCategoriesList}
                  onOpen={handleOpenPage}
                  onDelete={() => deletePage(page.id, user?.id)}
                  onDuplicate={() => duplicatePage(page.id, user?.id)}
                  onToggleStatus={() => toggleStatus(page.id, user?.id)}
                  onToggleVisibility={() => toggleVisibility(page.id, user?.id)}
                  onExportHTML={() => exportHTML(page.id, user?.id)}
                  onExportPDF={() => exportPDF(page.id, user?.id)}
                  onSetCategory={(cat) => handleUpdateCategory(page.id, cat)}
                  onOpenCoverModal={() => handleOpenCoverModal(page)}
                  onOpenCustomCategoryModal={() => handleOpenCustomCategoryModal(page)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="tut-list-tree">
          {rootPages.map(node => (
            <TreeRow
              key={node.id}
              node={node}
              depth={0}
              onOpen={handleOpenPage}
              onDelete={(id) => deletePage(id, user?.id)}
              onDuplicate={(id) => duplicatePage(id, user?.id)}
              onToggleStatus={(id) => toggleStatus(id, user?.id)}
              onToggleVisibility={(id) => toggleVisibility(id, user?.id)}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
            />
          ))}
        </div>
      )}

      {/* Cover Image Dialog */}
      <Dialog open={coverModalOpen} onOpenChange={setCoverModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus size={18} className="text-indigo-500" />
              Image de couverture
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Preview */}
            <div className="h-36 rounded-lg overflow-hidden border bg-muted flex items-center justify-center relative">
              {coverImageUrl ? (
                <>
                  <img src={coverImageUrl} alt="Aperçu" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setCoverImageUrl('')}
                    className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black text-white rounded-full text-xs"
                    title="Retirer la couverture"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <div className="text-center text-muted-foreground p-4">
                  <ImagePlus size={32} className="mx-auto mb-1 opacity-40" />
                  <p className="text-xs">Aucune image personnalisée. La bannière thématique par défaut sera affichée.</p>
                </div>
              )}
            </div>

            {/* Option 1: File upload */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1.5">
                Téléverser un fichier
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2 text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                  Choisir une image depuis l'ordinateur
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverFileUpload}
                />
              </div>
            </div>

            {/* Option 2: Image URL */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1.5">
                Ou coller une URL d'image
              </label>
              <div className="relative">
                <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={coverImageUrl}
                  onChange={(e) => setCoverImageUrl(e.target.value)}
                  placeholder="https://images.unsplash.com/photo-..."
                  className="pl-9 text-xs"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between items-center gap-2 pt-2 border-t">
            {coverImageUrl ? (
              <Button variant="ghost" size="sm" onClick={() => setCoverImageUrl('')} className="text-xs text-destructive">
                <Trash2 size={12} className="mr-1" /> Effacer
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCoverModalOpen(false)}>Annuler</Button>
              <Button size="sm" onClick={handleSaveCover} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Category Dialog */}
      <Dialog open={customCatModalOpen} onOpenChange={setCustomCatModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={18} className="text-indigo-500" />
              Nouvelle catégorie
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Saisissez le nom de la nouvelle catégorie ou du thème (ex: Ansible, Terraform, Security, Python...)
            </p>
            <Input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Nom de la catégorie..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateCustomCategory();
                }
              }}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setCustomCatModalOpen(false)}>Annuler</Button>
            <Button size="sm" onClick={handleCreateCustomCategory} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Créer la catégorie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Grid Card Component with Cover Banner & Actions
function PageCard({
  page, categoriesList, onOpen, onDelete, onDuplicate, onToggleStatus, onToggleVisibility,
  onExportHTML, onExportPDF, onSetCategory, onOpenCoverModal, onOpenCustomCategoryModal,
}) {
  const theme = getCategoryTheme(page.category);

  return (
    <Card
      className="tut-page-card group cursor-pointer hover:shadow-lg transition-all duration-200 border overflow-hidden flex flex-col h-full bg-card"
      onClick={() => onOpen(page.id)}
      data-testid={`tutorial-card-${page.id}`}
    >
      {/* Cover Header Image / Banner */}
      <div className="tut-card-cover-wrapper">
        {page.cover_image ? (
          <img
            src={page.cover_image}
            alt={page.title}
            className="tut-card-cover-img"
          />
        ) : (
          <div className={`tut-card-banner ${theme.bgClass}`}>
            <div className="tut-card-banner-pattern" />
            <div className="tut-card-banner-content">
              <span className="tut-card-banner-icon">{theme.icon}</span>
              <span className="tut-card-banner-watermark">{theme.watermarkText}</span>
            </div>
          </div>
        )}

        {/* Top Badges Overlay */}
        <div className="tut-card-cover-top">
          <Badge className={`tut-card-category-badge ${theme.badgeClass}`}>
            <span>{theme.icon}</span>
            <span>{theme.label}</span>
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white bg-black/40 hover:bg-black/70 backdrop-blur-md rounded-full border border-white/20 transition-opacity">
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenCoverModal(); }}>
                <ImagePlus size={14} className="mr-2 text-indigo-500" /> Modifier la couverture
              </DropdownMenuItem>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                  <Tag size={14} className="mr-2 text-indigo-500" /> Thème : {theme.label}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-48">
                  {categoriesList.filter(c => c.id !== 'all').map(cat => (
                    <DropdownMenuItem
                      key={cat.id}
                      onClick={(e) => { e.stopPropagation(); onSetCategory(cat.id); }}
                    >
                      <span className="mr-2">{cat.icon}</span> {cat.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenCustomCategoryModal(); }}>
                    <Plus size={14} className="mr-2 text-indigo-500" /> + Autre catégorie...
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                <Copy size={14} className="mr-2" /> Dupliquer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleStatus(); }}>
                <FileText size={14} className="mr-2" />
                {page.status === 'published' ? 'Passer en brouillon' : 'Publier'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}>
                {page.is_visible ? <EyeOff size={14} className="mr-2" /> : <Eye size={14} className="mr-2" />}
                {page.is_visible ? 'Masquer' : 'Rendre visible'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onExportHTML(); }}>
                <Download size={14} className="mr-2" /> Export HTML
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onExportPDF(); }}>
                <FileDown size={14} className="mr-2" /> Export PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
              >
                <Trash2 size={14} className="mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CardContent className="p-4 flex-1 flex flex-col justify-between">
        <div>
          {/* Header Title */}
          <div className="flex items-start gap-2 mb-2">
            <span className="text-xl flex-shrink-0 leading-none">{page.icon || '📄'}</span>
            <h3 className="font-semibold text-sm line-clamp-2 text-foreground group-hover:text-indigo-600 transition-colors">
              {page.title}
            </h3>
          </div>

          {/* Preview */}
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem] leading-relaxed">
            {page.preview || 'Aucun contenu rédigé pour le moment...'}
          </p>
        </div>

        <div>
          {/* Status Tags */}
          <div className="flex items-center justify-between pt-2 border-t border-border/60">
            <div className="flex items-center gap-1.5">
              <Badge
                variant={page.status === 'published' ? 'default' : 'secondary'}
                className={`text-[10px] py-0 h-5 font-medium ${page.status === 'published' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
              >
                {page.status === 'published' ? 'Publié' : 'Brouillon'}
              </Badge>
              {page.is_visible && (
                <Badge variant="outline" className="text-[10px] py-0 h-5 border-blue-500/30 text-blue-500 bg-blue-500/5">
                  <Eye size={9} className="mr-0.5" /> Visible
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock size={10} />
              {formatRelativeTime ? formatRelativeTime(page.updated_at) : page.updated_at?.slice(0, 10)}
            </div>
          </div>

          {/* Word count & author */}
          <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
            <span>{page.word_count || 0} mots</span>
            {page.author_name && (
              <>
                <span>•</span>
                <span className="flex items-center gap-0.5 truncate">
                  <User size={9} /> {page.author_name}
                </span>
              </>
            )}
            {page.children_count > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-0.5">
                  <FolderOpen size={9} /> {page.children_count} sous-pages
                </span>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Tree Row Component
function TreeRow({ node, depth, onOpen, onDelete, onDuplicate, onToggleStatus, onToggleVisibility, expandedNodes, toggleNode }) {
  const isExpanded = expandedNodes[node.id];
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <div
        className="tut-tree-row group"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
        onClick={() => onOpen(node.id)}
        data-testid={`tutorial-row-${node.id}`}
      >
        <button
          type="button"
          className="tut-tree-row-toggle"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleNode(node.id); }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronRight size={14} className="rotate-90 transition-transform" /> : <ChevronRight size={14} className="transition-transform" />
          ) : <span className="w-3.5" />}
        </button>
        <span className="text-base">{node.icon || '📄'}</span>
        <span className="text-sm font-medium flex-1 truncate">{node.title}</span>
        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Badge
            variant={node.status === 'published' ? 'default' : 'secondary'}
            className="text-[10px] py-0 h-4"
          >
            {node.status === 'published' ? 'Publié' : 'Brouillon'}
          </Badge>
          {node.is_visible && <Eye size={12} className="text-emerald-500" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <MoreHorizontal size={12} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(node.id); }}>
                <Copy size={14} className="mr-2" /> Dupliquer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleStatus(node.id); }}>
                {node.status === 'published' ? 'Brouillon' : 'Publier'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleVisibility(node.id); }}>
                {node.is_visible ? 'Masquer' : 'Rendre visible'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}>
                <Trash2 size={14} className="mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <span className="text-[10px] text-muted-foreground ml-2 whitespace-nowrap">
          {node.updated_at?.slice(0, 10)}
        </span>
      </div>
      {hasChildren && isExpanded && node.children.map(child => (
        <TreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          onOpen={onOpen}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onToggleStatus={onToggleStatus}
          onToggleVisibility={onToggleVisibility}
          expandedNodes={expandedNodes}
          toggleNode={toggleNode}
        />
      ))}
    </>
  );
}
